import { useState } from 'react'
import { Route, Router, useLocation } from 'wouter'
import { AppSidebar } from './components/AppSidebar'
import { FiscalWeekProvider } from './contexts/FiscalWeekContext'
import { AuthProvider } from './contexts/AuthContext'
import { TenantProvider } from './contexts/TenantContext'
import ProtectedRoute from './components/ProtectedRoute'
import HomePage from './pages/HomePage'
import Login from './pages/Login'
import DashboardRouter from './pages/DashboardRouter'
import Stores from './pages/Stores'
import InventoryManagementEnhanced from './pages/InventoryManagementEnhanced'
import SKUsDetail from './pages/SKUsDetail'
import GrossMarginDetail from './pages/GrossMarginDetail'
import InventoryCoverageDetail from './pages/InventoryCoverageDetail'
import ProductAnalysisImproved from './pages/ProductAnalysisImproved'
import RevenueDetailEnhanced from './pages/RevenueDetailEnhanced'
import UnitsDetailEnhanced from './pages/UnitsDetailEnhanced'
import StorePerformanceDeepDive from './pages/StorePerformanceDeepDive'
import StoreClusterIntelligence from './pages/StoreClusterIntelligence'
import PromoOptimizer from './pages/PromoOptimizer'
import InventoryIntelligence from './pages/InventoryIntelligence'
import { PricingMargin } from './pages/PricingMargin'
import { DistributionAvailability } from './pages/DistributionAvailability'
import { ConcentrationRisk } from './pages/ConcentrationRisk'
import RetailSupport from './pages/RetailSupport'
import RetailSupportOptimizer from './pages/RetailSupportOptimizer'
import ProductDimensions from './pages/ProductDimensions'
// PlanogramLabs merged into MerchOptimization - import removed
import AgentHub from './pages/AgentHub'
import CommandCenter from './pages/CommandCenter'
import AdminDataMonitor from './pages/AdminDataMonitor'
import DatabaseAudit from './pages/DatabaseAudit'
import CopilotFeedback from './pages/admin/CopilotFeedback'
// MLMonitoring now consolidated into MLDashboard (ML Command Center)
import KnowledgeBase from './pages/admin/KnowledgeBase'
import Profiles from './pages/admin/Profiles'
import TenantAgents from './pages/admin/TenantAgents'
import PoliciesPage from './pages/admin/Policies'
import WorkflowsPage from './pages/admin/Workflows'
import ApiKeysPage from './pages/admin/ApiKeys'
import IngestionConfig from './pages/admin/IngestionConfig'
// SocialListeningFeedback removed (no route)
import ReleaseHealth from './pages/admin/ReleaseHealth'
import ContextIntelligence from './pages/admin/ContextIntelligence'
import DtcSummary from './pages/dtc/Summary'
import DtcPlatformDetail from './pages/dtc/Overview'
import DtcAmazon from './pages/dtc/Amazon'
import DtcWalmart from './pages/dtc/Walmart'
import DtcPlatformSettings from './pages/dtc/PlatformSettings'
// ML Pages (ML Monitoring admin view only)
import MLDashboard from './pages/ml/MLDashboard'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'

// Phase H Alerts
import Alerts from './pages/Alerts'
import RetailStory from './pages/agents/RetailStory'
import NPDOpportunity from './pages/agents/NPDOpportunity'
import MerchOptimization from './pages/agents/MerchOptimization'
import SupplyChain from './pages/agents/SupplyChain'

// Advocate Engine (Opus 4.5 Build)
import RetailDashboard from './pages/RetailDashboard'
import RetailPerformance from './pages/RetailPerformance'
import IntelligenceHub from './pages/IntelligenceHub'
import SocialHalo from './pages/SocialHalo'
import CompetitiveRadar from './pages/CompetitiveRadar'
import BriefingDetail from './pages/BriefingDetail'
import AdvocateOpusOverview from './pages/advocate-opus/Overview'
import AdvocateOpusDiscovery from './pages/advocate-opus/Discovery'
import AdvocateOpusDiscoveryNew from './pages/advocate-opus/DiscoveryNew'
import AdvocateOpusAdvocatesList from './pages/advocate-opus/AdvocatesList'
import AdvocateOpusProfile from './pages/advocate-opus/AdvocateProfile'
import AdvocateOpusOffers from './pages/advocate-opus/Offers'
import AdvocateOpusPayouts from './pages/advocate-opus/Payouts'

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [location] = useLocation()
  
  // Check if current route is public (no sidebar/chat needed)
  const isPublicRoute = location === '/' || location === '/login'

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Public Routes - No Sidebar or Chat */}
      <Route path="/" component={HomePage} />
      <Route path="/login" component={Login} />
      
      {/* Protected Routes - With Sidebar and Chat */}
      {!isPublicRoute && (
        <ProtectedRoute>
          {/* Dashboard route ONLY - handles its own sidebar */}
          {location === '/dashboard' || location.startsWith('/dashboard?') ? (
            <Route path="/dashboard" component={DashboardRouter} />
          ) : (
            /* All other routes use standard sidebar */
            <div className="flex">
              <AppSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
              <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
                <main className="p-6">
                    <Route path="/stores" component={Stores} />
                    <Route path="/store-performance-deep-dive" component={StorePerformanceDeepDive} />
                    <Route path="/products" component={ProductAnalysisImproved} />
                    <Route path="/inventory" component={InventoryManagementEnhanced} />
                    <Route path="/admin/knowledge-base" component={KnowledgeBase} />
                    <Route path="/admin/profiles" component={Profiles} />
                    <Route path="/admin/tenant-agents" component={TenantAgents} />
                    <Route path="/admin/policies" component={PoliciesPage} />
                    <Route path="/admin/workflows" component={WorkflowsPage} />
                    <Route path="/admin/release-health">
                      {() => (
                        <RouteErrorBoundary title="Release & Health crashed">
                          <ReleaseHealth />
                        </RouteErrorBoundary>
                      )}
                    </Route>
                    <Route path="/admin/api-keys" component={ApiKeysPage} />
                    <Route path="/admin/data-monitor" component={AdminDataMonitor} />
                    <Route path="/admin/database-audit" component={DatabaseAudit} />
                    <Route path="/admin/copilot-feedback" component={CopilotFeedback} />
                    <Route path="/admin/ingestion-config" component={IngestionConfig} />
                    <Route path="/admin/context-intelligence" component={ContextIntelligence} />
                    <Route path="/retail" component={RetailDashboard} />
                    <Route path="/pricing-margin" component={PricingMargin} />
                    <Route path="/distribution-availability" component={DistributionAvailability} />
                    <Route path="/concentration-risk" component={ConcentrationRisk} />
                    <Route path="/retail-support" component={RetailSupport} />

                    {/* Retail Performance (Sell-In vs Sell-Through) */}
                    <Route path="/retail-performance" component={RetailPerformance} />
                    <Route path="/social-halo" component={SocialHalo} />

                    {/* Intelligence Hub */}
                    <Route path="/intelligence/:id" component={BriefingDetail} />
                    <Route path="/intelligence" component={IntelligenceHub} />
                    <Route path="/competitive-radar" component={CompetitiveRadar} />
                    
                    {/* DTC Portal */}
                    <Route path="/dtc/overview" component={DtcSummary} />
                    <Route path="/dtc/platform/:id" component={DtcPlatformDetail} />
                    <Route path="/dtc/shopify" component={DtcPlatformDetail} />
                    <Route path="/dtc/amazon" component={DtcAmazon} />
                    <Route path="/dtc/walmart" component={DtcWalmart} />
                    <Route path="/dtc/settings" component={DtcPlatformSettings} />
                    
                    {/* PatternIQ AI */}
                    <Route path="/command-center" component={CommandCenter} />
                    <Route path="/alerts" component={Alerts} />
                    <Route path="/agent-hub" component={AgentHub} />
                    
                    {/* Phase F Specialist Agents */}
                    <Route path="/agents/retail-story" component={RetailStory} />
                    <Route path="/agents/npd-opportunity" component={NPDOpportunity} />
                    <Route path="/agents/merch-optimization" component={MerchOptimization} />
                    <Route path="/agents/supply-chain" component={SupplyChain} />
                    <Route path="/copilot">
                      {() => {
                        window.location.replace('/agent-hub')
                        return null;
                      }}
                    </Route>
                    <Route path="/ai/gpt">
                      {() => {
                        window.location.replace('/agent-hub')
                        return null;
                      }}
                    </Route>
                    <Route path="/ai/retail-support-optimizer" component={RetailSupportOptimizer} />
                    <Route path="/ai/store-cluster-intelligence" component={StoreClusterIntelligence} />
                    <Route path="/ai/promo-optimizer" component={PromoOptimizer} />
                    <Route path="/ai/inventory-intelligence" component={InventoryIntelligence} />
                    <Route path="/product-dimensions" component={ProductDimensions} />
                    
                    {/* Deep Dive Pages */}
                    <Route path="/revenue-detail" component={RevenueDetailEnhanced} />
                    <Route path="/units-detail" component={UnitsDetailEnhanced} />
                    <Route path="/skus-detail" component={SKUsDetail} />
                    <Route path="/gross-margin-detail" component={GrossMarginDetail} />
                    <Route path="/inventory-coverage-detail" component={InventoryCoverageDetail} />
                    
                    {/* Admin ML Monitoring Route */}
                    <Route path="/admin/ml-monitoring" component={MLDashboard} />
                    
                    {/* Advocate Engine (Opus 4.5 Build) */}
                    <Route path="/advocate-opus" component={AdvocateOpusOverview} />
                    <Route path="/advocate-opus/discovery" component={AdvocateOpusDiscoveryNew} />
                    <Route path="/advocate-opus/discovery-old" component={AdvocateOpusDiscovery} />
                    <Route path="/advocate-opus/advocates" component={AdvocateOpusAdvocatesList} />
                    <Route path="/advocate-opus/advocates/:id" component={AdvocateOpusProfile} />
                    <Route path="/advocate-opus/offers" component={AdvocateOpusOffers} />
                    <Route path="/advocate-opus/payouts" component={AdvocateOpusPayouts} />
                </main>
              </div>
            </div>
          )}
        </ProtectedRoute>
      )}
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <FiscalWeekProvider>
          <Router>
            <AppContent />
          </Router>
        </FiscalWeekProvider>
      </TenantProvider>
    </AuthProvider>
  )
}

export default App
